'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var filterX = _interopDefault(require('ml-array-xy-filter-x'));
var equallySpaced = _interopDefault(require('ml-array-xy-equally-spaced'));
var Stat = _interopDefault(require('ml-stat/array'));
var mlSpectraProcessing = require('ml-spectra-processing');
var jcampconverter = require('jcampconverter');
var SimpleLinearRegression = _interopDefault(require('ml-regression-simple-linear'));

/**
 *
 * @private
 * @param {Spectrum} spectrum
 * @param {object} [filter={}]
 * @param {array} [filter.from]
 * @param {array} [filter.to]
 * @param {array} [filter.exclusions=[]]
 */

function getData(spectrum, filter = {}) {
  let data = { x: spectrum.x, y: spectrum.y };

  if (filter) {
    data = filterX({ x: spectrum.x, y: spectrum.y }, filter);
  }
  return data;
}

/**
 *
 * @private
 * @param {*} spectrum
 * @param {*} options
 */
function getNormalized(spectrum, options = {}) {
  // did the options change ?
  if (
    JSON.stringify(spectrum.normalizationOptions) === JSON.stringify(options)
  ) {
    return spectrum.normalized;
  }
  spectrum.normalizationOptions = JSON.parse(JSON.stringify(options));
  if (!Array.isArray(spectrum.x) || !Array.isArray(spectrum.y)) {
    throw new Error(
      'getNormalized: Can not get normalized data, missing original data.'
    );
  }

  let {
    from = spectrum.x[0],
    to = spectrum.x[spectrum.x.length - 1],
    numberOfPoints = 1024,
    filters = [],
    exclusions = []
  } = options;

  let y = spectrum.y.slice(0);

  for (let filter of filters) {
    switch (filter.name) {
      case 'centerMean': {
        let mean = Stat.mean(spectrum.y);
        let meanFct = (y) => y - mean;
        y = y.map(meanFct);
        break;
      }
      case 'scaleSD': {
        let std = Stat.standardDeviation(spectrum.y);
        let stdFct = (y) => y / std;
        y = y.map(stdFct);
        break;
      }
      case '':
      case undefined:
        break;
      default:
        throw new Error(`Unknown process kind: ${process.kind}`);
    }
  }

  let result = equallySpaced(
    { x: spectrum.x, y },
    { from, to, numberOfPoints, exclusions }
  );

  return result;
}

/**
 *  @private
 * @param {*} spectrum
 * @param {*} ranges
 */
function updateRangesInfo(spectrum, ranges = []) {
  spectrum.ranges = {};
  for (let range of ranges) {
    range = JSON.parse(JSON.stringify(range));
    spectrum.ranges[range.label] = range;
    let fromToIndex = {
      fromIndex: mlSpectraProcessing.X.findClosestIndex(spectrum.normalized.x, range.from),
      toIndex: mlSpectraProcessing.X.findClosestIndex(spectrum.normalized.x, range.to)
    };
    range.integration = mlSpectraProcessing.XY.integration(spectrum.normalized, fromToIndex);
    range.maxPoint = mlSpectraProcessing.XY.maxYPoint(spectrum.normalized, fromToIndex);
  }
}

/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.x=[]] - x values
 * @param {Array} [json.y=[]] - y values
 */
class Spectrum {
  /**
   *
   * @param {array} x
   * @param {array} y
   * @param {string} id
   * @param {object} [options={}]
   */
  constructor(x, y, id, options = {}) {
    const { meta = {}, normalization = {} } = options;
    if (x && x.length > 1 && x[0] > x[1]) {
      this.x = x.reverse();
      this.y = y.reverse();
    } else {
      this.x = x || [];
      this.y = y || [];
    }
    this.id = id;
    this.meta = meta;
    this.normalized = getNormalized(this, normalization);
    this.updateMemory();
  }

  updateMemory() {
    let original = ((this.x && this.x.length) || 0) * 16;
    let normalized = this.normalized.x.length * 16;
    this.memory = { original, normalized, total: original + normalized };
  }

  removeOriginal() {
    this.x = undefined;
    this.y = undefined;
    this.updateMemory();
  }

  getXY() {
    if (!Array.isArray(this.x) || !Array.isArray(this.y)) {
      throw new Error('Can not get normalized data');
    }
    return { x: this.x, y: this.y };
  }
}

Spectrum.prototype.getData = function (options) {
  return getData(this, options);
};

Spectrum.prototype.updateNormalization = function (normalization) {
  this.normalized = getNormalized(this, normalization);
  this.ranges = {};
  this.updateMemory();
};

Spectrum.prototype.updateRangesInfo = function (ranges) {
  updateRangesInfo(this, ranges);
};

function getJcampKind(data) {
  let datatype = data.spectra[0].dataType.toLowerCase();
  let yUnit = data.spectra[0].yUnit.toLowerCase();

  if (datatype.match(/infrared/)) {
    if (yUnit.match(/absorbance/)) {
      return IR_ABSORBANCE;
    } else {
      return IR_TRANSMITTANCE;
    }
  }
  return undefined;
}

const IR_TRANSMITTANCE = {
  normalization: {},
  importation: {
    converter: (transmittance) => -Math.log10(transmittance)
  },
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

const IR_ABSORBANCE = {
  normalization: {},
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function jcamp(jcamp) {
  const parsed = jcampconverter.convert(jcamp, { xy: true, keepRecordsRegExp: /TITLE/ });
  const kind = getJcampKind(parsed);
  const data = parsed.spectra[0].data[0];
  const meta = parsed.info;
  // we convert the data
  if (kind && kind.importation && kind.importation.converter) {
    data.y = data.y.map(kind.importation.converter);
  }

  return { data, kind, meta };
}

function getNormalizationAnnotations(filter = {}) {
  let { exclusions = [] } = filter;
  let annotations = [];
  exclusions = exclusions.filter((exclusion) => !exclusion.ignore);
  annotations = exclusions.map((exclusion) => {
    var annotation = {
      type: 'rect',
      position: [{ x: exclusion.from, y: -200 }, { x: exclusion.to, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    };
    return annotation;
  });
  if (filter.from !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: 0, y: -200 }, { x: filter.from, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  if (filter.to !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: filter.to, y: -200 }, { x: 10000, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  return annotations;
}

function addChartDataStyle(data, spectrum) {
  data.styles = {
    unselected: {
      lineColor: spectrum.meta.color || 'darkgrey',
      lineWidth: 1,
      lineStyle: 1
    },
    selected: {
      lineColor: spectrum.meta.color || 'darkgrey',
      lineWidth: 3,
      lineStyle: 1
    }
  };
  data.label = spectrum.meta.id || spectrum.id;
}

/**
 * Retrieve a chart with selected original data
 * @param {object} [options={}]
 * @param {Array} [options.ids] List of spectra ids, by default all
 * @param {Array} [options.maxDataPoints=]
 */
function getChart(spectra, options = {}) {
  const { ids } = options;
  let chart = {
    data: []
  };
  for (let spectrum of spectra) {
    if (!ids || ids.includes(spectrum.id)) {
      let data = spectrum.getData();
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

function getNormalizedChart(spectra, options = {}) {
  const { ids } = options;
  let chart = {
    data: []
  };
  for (let spectrum of spectra) {
    if (!ids || ids.includes(spectrum.id)) {
      let data = spectrum.normalized;
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

/**
 *
 * @param {SpectraProcessor} spectraProcessor
 * @param {object} [options={}] scale spectra based on various parameters
 * @param {object} [options.range] from - to
 * @param {Array} [options.ids] ids of selected spectra
 * @param {string} [options.targetID=spectra[0].id]
 * @param {string} [options.method='max'] min, max, range, minMax
 * @param {boolean} [options.relative=false]
 */
function getScaledChart(spectraProcessor, options = {}) {
  let scaled = spectraProcessor.getScaledData(options);

  let chart = {
    data: []
  };

  for (let i = 0; i < scaled.matrix.length; i++) {
    let data = {
      x: scaled.x,
      y: scaled.matrix[i]
    };
    addChartDataStyle(data, { meta: scaled.meta[i], id: scaled.ids[i] });
    chart.data.push(data);
  }

  return chart;
}

/**
 * @private
 * @param {*} spectra
 * @param {*} options
 */

function getNormalizedData(spectra) {
  if (!spectra || !spectra[0]) return {};
  let matrix = [];
  let meta = [];
  let currentIDs = [];

  for (let spectrum of spectra) {
    currentIDs.push(spectrum.id);
    matrix.push(spectrum.normalized.y);
    meta.push(spectrum.meta);
  }
  let x = spectra[0].normalized.x;
  return { ids: currentIDs, matrix, meta, x };
}

/**
 * @private
 * @param {*} spectra
 * @param {*} options
 */

function getNormalizedTSV(spectra) {
  let { matrix, meta, ids, x } = getNormalizedData(spectra);

  let allKeysObject = {};
  for (let metum of meta) {
    for (let key of Object.keys(metum)) {
      allKeysObject[key] = true;
    }
  }
  let allKeys = Object.keys(allKeysObject);

  let lines = [];
  let line = [];
  line.push('id', ...allKeys, ...x);
  lines.push(line.join('\t'));

  for (let i = 0; i < ids.length; i++) {
    line = [];
    line.push(ids[i]);
    for (let key of allKeys) {
      line.push(meta[i][key]);
    }
    line.push(...matrix[i]);
    lines.push(line.join('\t'));
  }

  return lines.join('\n');
}

function getFromToIndex(xs, range) {
  let { from, to } = range;
  if (from === undefined) {
    from = xs[0];
  }
  if (to === undefined) {
    to = xs[xs.length - 1];
  }

  return {
    fromIndex: mlSpectraProcessing.X.findClosestIndex(xs, from),
    toIndex: mlSpectraProcessing.X.findClosestIndex(xs, to)
  };
}

function min(spectra, targetSpectrum, range = {}) {
  let fromToIndex = getFromToIndex(targetSpectrum.normalized.x, range);

  let targetValue = mlSpectraProcessing.XY.minYPoint(targetSpectrum.normalized, fromToIndex).y;

  let values = spectra.map(
    (spectrum) => mlSpectraProcessing.XY.minYPoint(spectrum.normalized, fromToIndex).y
  );

  let matrix = [];
  for (let i = 0; i < spectra.length; i++) {
    let spectrum = spectra[i];
    let factor = targetValue / values[i];
    matrix.push(mlSpectraProcessing.X.multiply(spectrum.normalized.y, factor));
  }

  return matrix;
}

function max(spectra, targetSpectrum, range = {}) {
  let fromToIndex = getFromToIndex(targetSpectrum.normalized.x, range);

  let targetValue = mlSpectraProcessing.XY.maxYPoint(targetSpectrum.normalized, fromToIndex).y;

  let values = spectra.map(
    (spectrum) => mlSpectraProcessing.XY.maxYPoint(spectrum.normalized, fromToIndex).y
  );

  let matrix = [];
  for (let i = 0; i < spectra.length; i++) {
    let spectrum = spectra[i];
    let factor = targetValue / values[i];
    matrix.push(mlSpectraProcessing.X.multiply(spectrum.normalized.y, factor));
  }

  return matrix;
}

function minMax(spectra, targetSpectrum, range = {}) {
  let fromToIndex = getFromToIndex(targetSpectrum.normalized.x, range);

  let targetValue = {
    min: mlSpectraProcessing.XY.minYPoint(targetSpectrum.normalized, fromToIndex).y,
    max: mlSpectraProcessing.XY.maxYPoint(targetSpectrum.normalized, fromToIndex).y
  };

  let values = spectra.map((spectrum) => {
    return {
      min: mlSpectraProcessing.XY.minYPoint(spectrum.normalized, fromToIndex).y,
      max: mlSpectraProcessing.XY.maxYPoint(spectrum.normalized, fromToIndex).y
    };
  });

  let matrix = [];
  for (let i = 0; i < spectra.length; i++) {
    let spectrum = spectra[i];
    const regression = new SimpleLinearRegression(
      [targetValue.min, targetValue.max],
      [values[i].min, values[i].max]
    );

    let length = spectrum.normalized.y.length;
    let scaled = new Array(length);
    for (let j = 0; j < length; j++) {
      scaled[j] = regression.computeX(spectrum.normalized.y[j]);
    }
    matrix.push(scaled);
  }

  return matrix;
}

function range(spectra, targetSpectrum, range = {}) {
  let fromToIndex = getFromToIndex(targetSpectrum.normalized.x, range);

  let targetValue = mlSpectraProcessing.XY.integration(targetSpectrum.normalized, fromToIndex);

  let values = spectra.map((spectrum) =>
    mlSpectraProcessing.XY.integration(spectrum.normalized, fromToIndex)
  );

  let matrix = [];
  for (let i = 0; i < spectra.length; i++) {
    let spectrum = spectra[i];
    let factor = targetValue / values[i];
    matrix.push(mlSpectraProcessing.X.multiply(spectrum.normalized.y, factor));
  }

  return matrix;
}

/**
 * @private
 * @param {Array<Spectrum>} spectra
 * @param {object} [options={}] scale spectra based on various parameters
 * @param {object} [options.range] from - to
 * @param {Array} [options.ids] ids of selected spectra
 * @param {string} [options.targetID=spectra[0].id]
 * @param {string} [options.method='max'] min, max, range, minMax
 * @param {boolean} [options.relative=false]
 * @returns {object} { ids:[], matrix:[Array], meta:[object], x:[] }
 */

function getScaledData(spectraProcessor, options = {}) {
  if (!spectraProcessor.spectra || !spectraProcessor.spectra[0]) return {};
  const { range: range$1, targetID, relative, method, ids } = options;

  let targetSpectrum =
    spectraProcessor.getSpectrum(targetID) || spectraProcessor.spectra[0];
  let spectra = spectraProcessor.getSpectra(ids);

  let result;
  if (method === '' || method === undefined) {
    result = getNormalizedData(spectra);
  } else {
    let matrix;
    switch (method.toLowerCase()) {
      case 'min':
        matrix = min(spectra, targetSpectrum, range$1);
        break;
      case 'max':
        matrix = max(spectra, targetSpectrum, range$1);
        break;
      case 'minmax':
        matrix = minMax(spectra, targetSpectrum, range$1);
        break;
      case 'range':
        matrix = range(spectra, targetSpectrum, range$1);
        break;
      default:
        throw new Error(`getScaledData: unknown method: ${method}`);
    }
    let meta = [];
    let currentIDs = [];
    for (let spectrum of spectra) {
      currentIDs.push(spectrum.id);
      meta.push(spectrum.meta);
    }
    let x = spectra[0].normalized.x;
    result = { ids: currentIDs, matrix, meta, x };
  }

  if (relative) {
    for (let i = 0; i < result.matrix.length; i++) {
      result.matrix[i] = mlSpectraProcessing.X.subtract(
        result.matrix[i],
        targetSpectrum.normalized.y
      );
    }
  }

  return result;
}

class SpectraProcessor {
  /**
   * Manager a large number of spectra with the possibility to normalize the data
   * and skip the original data.
   * @param {object} [options={}]
   * @param {boolean} [options.maxMemory=64M]
   * @param {object} [options.normalization={}] options to normalize the spectra before comparison
   * @param {number} [options.normalization.from]
   * @param {number} [options.normalization.to]
   * @param {number} [options.normalization.numberOfPoints]
   * @param {array<object>} [options.normalization.filters]
   * @param {string} [options.normalization.filters.X.name]
   * @param {object} [options.normalization.filters.X.options]
   * @param {array<object>} [options.normalization.exclusions]
   * @param {string} [options.normalization.exclusions.X.from]
   * @param {object} [options.normalization.exclusions.X.to]
   */
  constructor(options = {}) {
    this.normalization = options.normalization;
    this.maxMemory = options.maxMemory || 64 * 1024 * 1024;
    this.keepOriginal = true;
    this.spectra = [];
  }

  getNormalizationAnnotations() {
    return getNormalizationAnnotations(this.normalization);
  }

  /**
   * Recalculate the normalized data using the stored original data if available
   * This will throw an error in the original data is not present
   * @param {number} [normalization.from]
   * @param {number} [normalization.to]
   * @param {number} [normalization.numberOfPoints]
   * @param {array<object>} [normalization.filters]
   * @param {string} [normalization.filters.X.name]
   * @param {object} [normalization.filters.X.options]
   * @param {array<object>} [normalization.exclusions]
   * @param {string} [normalization.exclusions.X.from]
   * @param {object} [normalization.exclusions.X.to]
   */
  setNormalization(normalization = {}) {
    this.normalization = normalization;
    for (let spectrum of this.spectra) {
      spectrum.updateNormalization(this.normalization);
    }
  }

  /**
   * Returns an object contains 4 parameters with the normalized data
   * @returns {object} { ids:[], matrix:[Array], meta:[object], x:[] }
   */
  getNormalizedData() {
    return getNormalizedData(this.spectra);
  }

  /**
   * Returns a tab separated value containing the normalized data
   * @returns {string}
   */
  getNormalizedTSV() {
    return getNormalizedTSV(this.spectra);
  }

  /**

   * Returns an object contains 4 parameters with the scaled data
   * @param {object} [options={}] scale spectra based on various parameters
   * @param {object} [options.range] from - to
   * @param {Array} [options.ids] ids of selected spectra, by default all
   * @param {string} [options.targetID=spectra[0].id]
   * @param {string} [options.method='max'] min, max, range, minMax
   * @param {boolean} [options.relative=false]
   * @returns {object} { ids:[], matrix:[Array], meta:[object], x:[] }
   */
  getScaledData(options) {
    return getScaledData(this, options);
  }

  /**
   * Add jcamp
   * By default TITLE from the jcamp will be in the meta information
   * @param {string} jcamp
   * @param {object} [options={}]
   * @param {object} [options.meta={}]
   * @param {string} [options.meta.color]
   * @param {object} [options.id={}]
   * @param {boolean} [options.force=false] replace existing spectrum (same ID)
   */

  addFromJcamp(jcamp$1, options = {}) {
    if (options.force !== true && options.id && this.contains(options.id)) {
      return;
    }
    let parsed = jcamp(jcamp$1);
    let meta = { ...parsed.meta, ...(options.meta || {}) };
    this.addFromData(parsed.data, { meta, id: options.id });
  }

  updateRangesInfo(options) {
    for (let spectrum of this.spectra) {
      spectrum.updateRangesInfo(options);
    }
  }

  /**
   * Add a spectrum based on the data
   * @param {object} data {x, y, id, {meta, normalization}}
   * @param {object} [options={}]
   * @param {object} [options.meta={}]
   * @return {Spectrum}
   */

  addFromData(data, options = {}) {
    if (this.spectra.length === 0) this.keepOriginal = true;
    const id = options.id || Math.random(0).toString(36).substring;
    let index = this.getSpectrumIndex(id);
    if (index === undefined) index = this.spectra.length;
    let spectrum = new Spectrum(data.x, data.y, id, {
      meta: options.meta,
      normalization: this.normalization
    });
    this.spectra[index] = spectrum;
    if (!this.keepOriginal) {
      spectrum.removeOriginal();
    } else {
      let memoryInfo = this.getMemoryInfo();
      if (memoryInfo.total > this.maxMemory) {
        this.keepOriginal = false;
        this.removeOriginals();
      }
    }
  }

  removeOriginals() {
    for (let spectrum of this.spectra) {
      spectrum.removeOriginal();
    }
  }

  /**
   * Remove the spectrum from the SpectraProcessor for the specified id
   * @param {string} id
   */
  removeSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra.splice(index, 1);
  }

  /**
   * Remove all the spectra not present in the list
   * @param {Array} [ids] Array of ids of the spectra to keep
   */
  removeSpectraNotIn(ids) {
    let currentIDs = this.spectra.map((spectrum) => spectrum.id);
    for (let id of currentIDs) {
      if (!ids.includes(id)) {
        this.removeSpectrum(id);
      }
    }
  }

  /**
   * Checks if the ID of a spectrum exists in the SpectraProcessor
   * @param {string} id
   */
  contains(id) {
    return !isNaN(this.getSpectrumIndex(id));
  }

  /**
   * Returns the index of the spectrum in the spectra array
   * @param {string} id
   * @returns {number}
   */
  getSpectrumIndex(id) {
    if (!id) return undefined;
    for (let i = 0; i < this.spectra.length; i++) {
      let spectrum = this.spectra[i];
      if (spectrum.id === id) return i;
    }
    return undefined;
  }

  /**
   * Returns a spectrum from its ID
   * @param {Array} ids
   * @returns {Array<Spectrum}
   */
  getSpectra(ids) {
    if (!ids || !Array.isArray(ids) || ids.length === 0) return this.spectra;
    let spectra = [];
    for (let id of ids) {
      let index = this.getSpectrumIndex(id);
      if (index !== undefined) {
        spectra.push(this.spectra[index]);
      }
    }
    return spectra;
  }

  /**
   * Returns the index of the spectrum in the spectra array
   * @param {string} id
   * @returns {number}
   */
  getSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra[index];
  }

  /**
   * Returns a JSGraph chart object for all the spectra
   * @returns {object}
   */
  getChart() {
    return getChart(this.spectra);
  }

  /**
   * Returns a JSGraph chart object for all the normalized spectra
   * @param {object} [options={}]
   * @param {Array} [options.ids] ids of the spectra to select, by default all
   * @returns {object}
   */
  getNormalizedChart(options) {
    return getNormalizedChart(this.spectra, options);
  }

  /**
   * Returns a JSGraph chart object for all the scaled normalized spectra
   * @param {object} [options={}]
   * @param {Array} [options.ids] ids of the spectra to select, by default all
   * @param {object} [options.range] from - to
   * @param {string} [options.targetID=spectra[0].id]
   * @param {string} [options.method='max'] min, max, range, minMax
   * @param {boolean} [options.relative=false]
   * @returns {object}
   */
  getScaledChart(options) {
    return getScaledChart(this, options);
  }

  getMemoryInfo() {
    let memoryInfo = { original: 0, normalized: 0, total: 0 };
    this.spectra.forEach((spectrum) => {
      let memory = spectrum.memory;
      memoryInfo.original += memory.original;
      memoryInfo.normalized += memory.normalized;
      memoryInfo.total += memory.total;
    });
    memoryInfo.keepOriginal = this.keepOriginal;
    memoryInfo.maxMemory = this.maxMemory;
    return memoryInfo;
  }
}

exports.SpectraProcessor = SpectraProcessor;
